import React, { useEffect, useState } from 'react';
import { IconControl } from 'spark-core-dx/components';
import { useCoreContext, useCoreStateContext } from 'spark-core-dx/contexts';
import { ItemService } from 'spark-core-dx/services';
import './status.scss';

const AUTH_PROVIDER_LABELS = {
  0: 'AuthGuard',
  1: 'Entra ID',
  99999: 'None',
};

const hasDisplayValue = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null;
};

const getFirstDisplayValue = (...values) => values.find((value) => hasDisplayValue(value));

const formatSummaryValue = (value, formatter) => {
  if (formatter) {
    return formatter(value);
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Enabled' : 'Disabled';
  }

  return String(value);
};

const createSummaryEntry = ({ key, label, value, formatter, meta }) => {
  if (!hasDisplayValue(value)) {
    return null;
  }

  return {
    key,
    label,
    value: formatSummaryValue(value, formatter),
    meta: hasDisplayValue(meta) ? meta : null,
  };
};

const buildConnectionSections = (configItem) => {
  const hasBatchSubscriptionOverride = hasDisplayValue(configItem?.AzureBatchSubscriptionId);
  const hasBatchTenantOverride = hasDisplayValue(configItem?.AzureBatchTenantId);
  const hasBatchClientOverride = hasDisplayValue(configItem?.AzureBatchClientId);
  const hasBatchOrgOverride = hasDisplayValue(configItem?.AzureBatchOrgName);

  return [
    {
      key: 'data',
      title: 'Data',
      entries: [
        createSummaryEntry({
          key: 'core',
          label: 'Core MongoDB',
          value: configItem?.MongoDbConnectionStringCore,
        }),
        createSummaryEntry({
          key: 'portal',
          label: 'Portal MongoDB',
          value: configItem?.MongoDbConnectionStringPortal,
        }),
        createSummaryEntry({
          key: 'authguard',
          label: 'AuthGuard MongoDB',
          value: configItem?.MongoDbConnectionStringAuthGuard,
        }),
        createSummaryEntry({
          key: 'redis',
          label: 'Redis Cache',
          value: configItem?.RedisCacheConnectionString,
        }),
      ].filter(Boolean),
    },
    {
      key: 'auth',
      title: 'Auth',
      entries: [
        createSummaryEntry({
          key: 'auth-org-name',
          label: 'Org Name',
          value: configItem?.OrgName,
        }),
        createSummaryEntry({
          key: 'default-auth-provider',
          label: 'Default Provider',
          value: configItem?.DefaultAuthProvider,
          formatter: (value) => AUTH_PROVIDER_LABELS[value] ?? String(value),
        }),
        createSummaryEntry({
          key: 'core-auth-issuer',
          label: 'Core Auth Issuer',
          value: configItem?.CoreAuthServerIssuer,
        }),
        createSummaryEntry({
          key: 'entra-app',
          label: 'Entra App Client ID',
          value: configItem?.AZEntraIDAppClientId,
        }),
        createSummaryEntry({
          key: 'graph-app',
          label: 'Graph Client ID',
          value: configItem?.AZGraphClientId,
        }),
        createSummaryEntry({
          key: 'reporting-app',
          label: 'Reporting Client ID',
          value: configItem?.AZReportingAppClientId,
        }),
      ].filter(Boolean),
    },
    {
      key: 'storage',
      title: 'Storage',
      entries: [
        createSummaryEntry({
          key: 'queue-storage',
          label: 'Queue Storage',
          value: configItem?.AzureQueueStorageConnectionString,
        }),
        createSummaryEntry({
          key: 'dashboard-storage',
          label: 'Dashboard Storage',
          value: configItem?.AzureDashboardStorageConnectionString,
        }),
        createSummaryEntry({
          key: 'file-storage-account',
          label: 'Blob Storage Account',
          value: configItem?.AzureFileStorageAccountName,
        }),
        createSummaryEntry({
          key: 'file-storage-container',
          label: 'Blob Storage Container',
          value: configItem?.AzureFileStorageContainer,
        }),
      ].filter(Boolean),
    },
    {
      key: 'azure-batch',
      title: 'Azure Batch',
      entries: [
        createSummaryEntry({
          key: 'batch-org',
          label: 'Org Name',
          value: getFirstDisplayValue(configItem?.AzureBatchOrgName, configItem?.OrgName),
          meta: !hasBatchOrgOverride && hasDisplayValue(configItem?.OrgName)
            ? 'Using application org fallback'
            : null,
        }),
        createSummaryEntry({
          key: 'batch-url-prefix',
          label: 'URL Prefix',
          value: configItem?.AzureBatchUrlPrefix,
        }),
        createSummaryEntry({
          key: 'batch-subscription',
          label: 'Subscription ID',
          value: getFirstDisplayValue(configItem?.AzureBatchSubscriptionId, configItem?.AzureSubscriptionId),
          meta: !hasBatchSubscriptionOverride && hasDisplayValue(configItem?.AzureSubscriptionId)
            ? 'Using Azure Subscription fallback'
            : null,
        }),
        createSummaryEntry({
          key: 'batch-tenant',
          label: 'Tenant ID',
          value: getFirstDisplayValue(configItem?.AzureBatchTenantId, configItem?.AZTenant),
          meta: !hasBatchTenantOverride && hasDisplayValue(configItem?.AZTenant)
            ? 'Using Azure Tenant fallback'
            : null,
        }),
        createSummaryEntry({
          key: 'batch-client',
          label: 'Client ID',
          value: getFirstDisplayValue(configItem?.AzureBatchClientId, configItem?.AZClientId),
          meta: !hasBatchClientOverride && hasDisplayValue(configItem?.AZClientId)
            ? 'Using shared Azure client fallback'
            : null,
        }),
      ].filter(Boolean),
    },
    {
      key: 'other-connections',
      title: 'Other Connections',
      entries: [
        createSummaryEntry({
          key: 'snowflake',
          label: 'Snowflake',
          value: configItem?.SnowflakeConnectionString,
        }),
        createSummaryEntry({
          key: 'buildops',
          label: 'BuildOps',
          value: configItem?.BuildOpsConnectionString,
        }),
        createSummaryEntry({
          key: 'sage',
          label: 'Sage',
          value: configItem?.SageConnectionString,
        }),
        createSummaryEntry({
          key: 'ef-1',
          label: 'Entity Framework',
          value: configItem?.EFConnectionString1,
        }),
        createSummaryEntry({
          key: 'salesforce',
          label: 'Salesforce',
          value: configItem?.SalesforceRestConnectionString,
        }),
        createSummaryEntry({
          key: 'docusign',
          label: 'DocuSign',
          value: configItem?.DocuSignConnectionString,
        }),
        createSummaryEntry({
          key: 'pipedrive',
          label: 'Pipedrive',
          value: configItem?.PipedriveConnectionString,
        }),
      ].filter(Boolean),
    },
  ].filter((section) => section.entries.length > 0);
};

const copyText = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement('textarea');
  input.value = text;
  input.setAttribute('readonly', 'readonly');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
};

const JsonPanel = ({ title, iconKey, value, emptyMessage }) => {
  const hasValue = value !== undefined && value !== null;
  const [copyState, setCopyState] = useState('idle');

  useEffect(() => {
    if (copyState === 'idle') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setCopyState('idle');
    }, 1800);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [copyState]);

  const handleCopy = async () => {
    if (!hasValue) {
      return;
    }

    try {
      await copyText(JSON.stringify(value, null, 2));
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };

  const copyLabel = copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Copy Failed' : 'Copy';

  return (
    <section className="status-panel">
      <div className="status-panel-header">
        <div className="status-panel-title">
          <IconControl iconKey={iconKey} />
          <span>{title}</span>
        </div>
        <div className="status-panel-header__actions">
          <button
            type="button"
            className={`status-copy-btn${copyState === 'error' ? ' error' : ''}`}
            onClick={handleCopy}
            disabled={!hasValue}
          >
            {copyLabel}
          </button>
        </div>
      </div>
      {hasValue ? (
        <div className="status-json-wrap">
          <pre className="status-json">{JSON.stringify(value, null, 2)}</pre>
        </div>
      ) : (
        <div className="status-empty">{emptyMessage}</div>
      )}
    </section>
  );
};

const Status = () => {
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const [snapshot, setSnapshot] = useState({
    loading: true,
    error: null,
    payload: null,
  });

  useEffect(() => {
    let isMounted = true;

    const loadSnapshot = async () => {
      const profileService = new ItemService('Items/Profile', coreState, coreContext, 'CORE', 30000, true);

      setSnapshot((previous) => ({
        ...previous,
        loading: true,
        error: null,
      }));

      try {
        const response = await profileService.getOperation({
          operationType: 'StatusConfigSnapshot',
          isWarning: true,
          supressAllErrors: true,
        });
        const result = response?.first?.() ?? response?.[0];
        const payload = result?.Items?.first?.() ?? result?.Items?.[0];

        if (!result?.Success) {
          throw new Error(result?.DisplayMessage ?? result?.Message?.DisplayMessage ?? 'Unable to load status snapshot.');
        }

        if (isMounted) {
          setSnapshot({
            loading: false,
            error: null,
            payload,
          });
        }
      } catch (error) {
        if (isMounted) {
          setSnapshot({
            loading: false,
            error: error?.message ?? 'Unable to load status snapshot.',
            payload: null,
          });
        }
      }
    };

    loadSnapshot();

    return () => {
      isMounted = false;
    };
  }, [coreContext, coreState]);

  const configItem = snapshot.payload?.ConfigItem ?? {};
  const environmentLabel = getFirstDisplayValue(
    configItem?.EnvironmentWithLocal,
    configItem?.Environment,
    coreContext?.Launch?.Env,
  ) ?? 'Unknown';
  const connectionSections = buildConnectionSections(configItem);

  return (
    <div className="status-screen">
      <section className="table-top-row status-page-header">
        <div className="status-page-header__row">
          <div className="status-page-header__copy">
            <div className="table-title-container">
              <IconControl width="2rem" height="2rem" iconKey="system" />
              <div className="table-title-wrapper">
                <h2 className="table-title">Status</h2>
              </div>
            </div>
            <div className="status-page-header__subtitle">
              Live app configuration and the server snapshot for the app you are currently using.
            </div>
          </div>
        </div>
      </section>

      <section className="status-panel status-summary">
        <div className="status-panel-header">
          <div className="status-panel-title">
            <IconControl iconKey="info" />
            <span>{`Connection Summary for ${environmentLabel}`}</span>
          </div>
        </div>

        <div className="status-summary-scroll">
          <div className="status-summary-grid">
            {connectionSections.length > 0 ? (
              connectionSections.map((section) => (
                <section key={section.key} className="status-summary-section">
                  <div className="status-summary-section__header">
                    <span className="status-summary-section__title">{section.title}</span>
                    <span className="status-summary-section__count">{section.entries.length}</span>
                  </div>
                  <div className="status-summary-section__entries">
                    {section.entries.map((entry) => (
                      <div key={entry.key} className="status-summary-entry">
                        <span className="status-summary-entry__label">{entry.label}</span>
                        <div className="status-summary-entry__value-wrap">
                          <span className="status-summary-entry__value">{entry.value}</span>
                        </div>
                        {entry.meta ? (
                          <span className="status-summary-entry__meta">{entry.meta}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ))
            ) : (
              <div className="status-empty">
                {snapshot.loading
                  ? 'Loading connection summary...'
                  : 'No connection-oriented settings were found in the current config snapshot.'}
              </div>
            )}
          </div>
        </div>

        {snapshot.error && <div className="status-error">{snapshot.error}</div>}
      </section>

      <div className="status-grid">
        <JsonPanel
          title="System Settings"
          iconKey="system"
          value={snapshot.payload?.SystemConfig}
          emptyMessage="SystemConfig has not been loaded yet."
        />
        <JsonPanel
          title="Config"
          iconKey="config"
          value={snapshot.payload?.ConfigItem}
          emptyMessage="ConfigItem has not been loaded yet."
        />
      </div>
    </div>
  );
};

export default Status;
